'use client';

import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useState } from 'react';
import { useToast } from './ui/use-toast';
import { OrderResponse } from '@/models/Order';
import { Endpoints } from '@/lib/Endpoints';
import { Requester } from '@/lib/Requester';
import { GenericResponse } from '@/models/GenericResponse';
import { ErrorHandler } from '@/lib/ErrorHandler';
import { getLocalUser } from '@/lib/LocalStorage';
import Image from 'next/image';
import {
  formatPhoneNumber,
  handlePhoneNumberChange,
  handlePhoneNumberKeyDown,
  phoneRegex,
} from '@/lib/PhoneUtils';
import { useForm } from 'react-hook-form';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from './ui/form';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Spinner } from './ui/spinner';
import { InputOTP, InputOTPGroup, InputOTPSlot } from './ui/input-otp';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { useRouter } from 'next/navigation';
import { WebLink } from '@/lib/WebLinks';
import { StatusCode } from '@/lib/StatusCode';
import { GTMEventType } from '@/lib/GTMEventType';
import { sendGTMEvent } from '@/lib/gtm-wrapper';
import { getBasketEcommerceBody } from '@/lib/GTMEventType';
import { getBasket, setLastPaymentType } from '@/lib/BasketStorage';
import SmartImage from './SmartImage';
import { Checkbox } from './ui/checkbox';
import PrivacyDialog from './PrivacyDialog';

export function CheckoutPaymentTypeVodafone({ paymentType }: { paymentType: number }) {
  const router = useRouter();

  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [verifyDialogOpen, setVerifyDialogOpen] = useState(false);
  const [activateVodafonePAymentDialogOpen, setActivateVodafonePaymentDialogOpen] = useState(false);
  const [verifyOTPLoading, setVerifyOTPLoading] = useState(false);
  const [privacyChecked, setPrivacyChecked] = useState(false);
  const [isPrivacyDialogOpen, setPrivacyDialogOpen] = useState(false);

  const FormSchema = z.object({
    phone: z
      .string()
      .transform((val) => val.replace(/\D/g, '')) // Remove non-numeric characters
      .refine((val) => phoneRegex.test(val), {
        message: 'Telefon numaranızı doğru bir biçimde giriniz',
      }),
  });

  const form = useForm<z.infer<typeof FormSchema>>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      phone: '',
    },
  });

  useEffect(() => {
    const localUser = getLocalUser();
    if (localUser && localUser.msisdn) {
      try {
        const phone = formatPhoneNumber(localUser.msisdn.replace('+90', ''));
        form.setValue('phone', phone);
      } catch (e) {}
    }
  }, []);

  async function onSubmit(values: z.infer<typeof FormSchema>) {
    if (isLoading) return;

    if (!privacyChecked) {
      toast({
        variant: 'destructive',
        description: 'Gizlilik sözleşmesini kabul etmelisiniz',
      });
      return;
    }

    const basket = getBasket();
    setLastPaymentType('Vodafone');
    if (basket) {
      sendGTMEvent({
        event: GTMEventType.ADD_PAYMENT_INFO,
        ecommerce: {
          ...getBasketEcommerceBody(basket),
          payment_type: 'Vodafone',
        },
      });
    }

    setIsLoading(true);
    try {
      const body = {
        paymentType,
        paymentInformation: {
          msisdn: values.phone.replace(/\D/g, ''),
        },
      };
      const { data: result } = await Requester.post<GenericResponse<OrderResponse>>(
        Endpoints.START_ORDER,
        body,
      );
      if (result?.meta?.statusCode === 0) {
        setVerifyDialogOpen(true);
      }
    } catch (error) {
      const { message, statusCode } = ErrorHandler.getMessage(error);
      toast({
        variant: 'destructive',
        description: message,
      });
      if (statusCode === StatusCode.VODAFONE_PAYMENT_PASSIVE) {
        setActivateVodafonePaymentDialogOpen(true);
      }
    } finally {
      setIsLoading(false);
    }
  }

  async function verifyOTP(otp: string) {
    if (verifyOTPLoading) return;

    if (otp.length != 4) {
      toast({
        variant: 'destructive',
        description: 'Şifre 4 haneli olmalı',
      });
      return;
    }

    try {
      setVerifyOTPLoading(true);

      const body = {
        otp,
        paymentType,
        msisdn: form.getValues('phone')?.replace(/\D/g, '') || '',
      };
      const { data: result } = await Requester.post<GenericResponse<any>>(
        Endpoints.COMPLETE_ORDER,
        body,
      );
      if (result?.meta?.statusCode === 0) {
        setVerifyDialogOpen(false);
        router.replace(WebLink.ORDER_RECEIVED);
      }
    } catch (error) {
      const { message } = ErrorHandler.getMessage(error);
      toast({
        variant: 'destructive',
        description: message,
      });
    } finally {
      setVerifyOTPLoading(false);
    }
  }

  return (
    <div className="flex flex-col gap-4 sm:gap-8 justify-center min-h-40 items-center">
      <SmartImage
        src="/logo-vodafone-pay.png"
        alt="Vodafone Mobil Ödeme"
        width={192}
        height={140}
        unoptimized
        imageType="custom"
      />
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <FormField
            control={form.control}
            name="phone"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Vodafone Telefon Numarası</FormLabel>
                <FormControl>
                  <Input
                    value={field.value}
                    placeholder="(5__) ___-____"
                    className="h-[44px] border-hzc_beige_300 placeholder:text-hzc_beige_600 placeholder:font-normal placeholder:text-sm rounded-md"
                    onChange={(e) => {
                      handlePhoneNumberChange(field.onChange, e);
                    }}
                    onKeyDown={(e) => {
                      handlePhoneNumberKeyDown(field.onChange, e);
                    }}
                  />
                </FormControl>
                <FormMessage className="text-xs" />
              </FormItem>
            )}
          />
        </form>
        <div className="flex items-center gap-2">
          <Checkbox
            className="w-4 h-4 flex-shrink-0 "
            checked={privacyChecked}
            onCheckedChange={(isChecked) => setPrivacyChecked(isChecked === true)}
          />
          <span className="text-sm text-hzc_beige_700">
            <span
              className="font-medium hover:underline hover:cursor-pointer"
              onClick={() => setPrivacyDialogOpen(true)}
            >
              Satış Sözleşmesi
            </span>
            &apos;ni okudum ve kabul ediyorum. *
          </span>
          <PrivacyDialog open={isPrivacyDialogOpen} onOpenChange={setPrivacyDialogOpen} />
        </div>
      </Form>
      <Button
        className="w-full mx-auto md:w-1/3 bg-hzc_purple_500 hover:bg-hzc_purple_500 hover:scale-105 transition-all duration-300 rounded-full gap-2"
        onClick={form.handleSubmit(onSubmit)}
      >
        {isLoading && <Spinner className="size-6 text-white" />}
        <span className="text-hzc_beige_100">SMS Gönder</span>
      </Button>
      <VerifyCodeDialog
        open={verifyDialogOpen}
        onOpenChange={setVerifyDialogOpen}
        phone={form.getValues('phone') || ''}
        verifyOTP={verifyOTP}
        isLoading={verifyOTPLoading}
      />
      <VodafonePaymentActivateAlert
        open={activateVodafonePAymentDialogOpen}
        onOpenChange={setActivateVodafonePaymentDialogOpen}
        phone={form.getValues('phone') || ''}
      />
    </div>
  );
}

function VerifyCodeDialog({
  open,
  onOpenChange,
  phone,
  verifyOTP,
  isLoading,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  phone: string;
  verifyOTP: (otp: string) => void;
  isLoading: boolean;
}) {
  const [value, setValue] = useState('');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-[425px]"
        onInteractOutside={(e) => {
          e.preventDefault();
        }}
      >
        <DialogHeader>
          <DialogTitle className="text-[32px] text-hzc_purple_500">
            Vodafone Faturama Yansıt
          </DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-2">
          <div className="text-start text-sm text-hzc_beige_900">
            <span className="font-medium">{phone}</span> Numarasına gönderilen 4 haneli şifreyi
            giriniz.
          </div>
          <InputOTP maxLength={4} value={value} onChange={(value) => setValue(value)}>
            <InputOTPGroup className="mx-auto">
              <InputOTPSlot index={0} />
              <InputOTPSlot index={1} />
              <InputOTPSlot index={2} />
              <InputOTPSlot index={3} />
            </InputOTPGroup>
          </InputOTP>
        </div>
        <DialogFooter className="gap-2">
          <div className="flex flex-row gap-4 justify-end">
            <Button
              className="text-hzc_beige_900 border-1 border-[#5E6AFF1A] bg-[#FFFFFF] hover:bg-[#FFFFFF] transition-all duration-300 hover:scale-105 rounded-full h-[48px]"
              onClick={() => onOpenChange(false)}
            >
              Kapat
            </Button>
            <Button
              className="text-hzc_purple_500 bg-[#5E6AFF1A] hover:bg-[#5E6AFF1A] transition-all duration-300 hover:scale-105 rounded-full h-[48px] gap-2"
              onClick={() => verifyOTP(value)}
            >
              {isLoading && <Spinner className="size-6 text-white" />}
              Onayla
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function VodafonePaymentActivateAlert({
  open,
  onOpenChange,
  phone,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  phone: string;
}) {
  const { toast } = useToast();
  const [isOpenSMSSended, setIsOpenSMSSended] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [otp, setOpt] = useState('');

  async function sendOtp() {
    if (isLoading) return;

    try {
      setIsLoading(true);

      const body = {
        msisdn: phone.replace(/\D/g, '') || '',
      };
      const { data: result } = await Requester.post<GenericResponse<any>>(
        Endpoints.VODAFONE_SEND_ACTIVATION_OTP,
        body,
      );
      if (result?.meta?.statusCode === 0) {
        setIsOpenSMSSended(true);
        toast({
          variant: 'default',
          description: 'SMS ile gelen kod ile Vodafone Ödeme yöntemini aktifleştirebilirsiniz.',
        });
      }
    } catch (error) {
      const { message } = ErrorHandler.getMessage(error);
      toast({
        variant: 'destructive',
        description: message,
      });
    } finally {
      setIsLoading(false);
    }
  }

  async function verifyOTP() {
    if (isLoading) return;

    if (otp.length != 4) {
      toast({
        variant: 'destructive',
        description: 'Şifre 4 haneli olmalı',
      });
      return;
    }

    try {
      setIsLoading(true);

      const body = {
        otp: Number(otp),
        msisdn: phone.replace(/\D/g, '') || '',
      };
      const { data: result } = await Requester.post<GenericResponse<any>>(
        Endpoints.VODAFONE_ACTIVATE,
        body,
      );
      if (result?.meta?.statusCode === 0) {
        onOpenChange(false);
        toast({
          variant: 'default',
          description:
            'Hattınız mobil ödemelere açılmıştır. Satın alma işlemine devam edebilirsiniz.',
        });
      }
    } catch (error) {
      const { message } = ErrorHandler.getMessage(error);
      toast({
        variant: 'destructive',
        description: message,
      });
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle className="text-[32px] text-hzc_purple_500">
            Vodafone Faturama Yansıt
          </DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-2">
          <div className="text-start text-sm text-hzc_beige_900">
            <span className="font-medium">{phone} </span>
            numaralı Vodafone müşterisinin &ldquo;Faturama Yansıtma&ldquo; özelliği aktif değil. Bu
            numaraya göndereceğimiz SMS&apos;te yer alan kodu girerek aktif edebilirsin.
          </div>
          {isOpenSMSSended && (
            <InputOTP maxLength={4} value={otp} onChange={(value) => setOpt(value)}>
              <InputOTPGroup className="mx-auto">
                <InputOTPSlot index={0} />
                <InputOTPSlot index={1} />
                <InputOTPSlot index={2} />
                <InputOTPSlot index={3} />
              </InputOTPGroup>
            </InputOTP>
          )}
        </div>
        <DialogFooter className="gap-2">
          <div className="flex flex-row gap-4 justify-end">
            <Button
              className="text-hzc_beige_900 border-1 border-[#5E6AFF1A] bg-[#FFFFFF] hover:bg-[#FFFFFF] transition-all duration-300 hover:scale-105 rounded-full h-[48px]"
              onClick={() => onOpenChange(false)}
            >
              Kapat
            </Button>
            <Button
              className="text-hzc_purple_500 bg-[#5E6AFF1A] hover:bg-[#5E6AFF1A] transition-all duration-300 hover:scale-105 rounded-full h-[48px] gap-2"
              onClick={() => {
                if (!isOpenSMSSended) {
                  sendOtp();
                } else {
                  verifyOTP();
                }
              }}
            >
              {isLoading && <Spinner className="size-6 text-white" />}

              {isOpenSMSSended ? 'Onayla' : 'Kod al'}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
